import type { ReactNode } from 'react';
import { IoWarningOutline } from 'react-icons/io5';
import { HiOutlineCheck } from 'react-icons/hi2';
import { ActionIcon, Button, Popover, Text, Title } from 'rizzui';
import cn from '../../utils/classNames';

export type ConfirmActionPopoverProps = {
    title: string;
    description: string;
    onConfirm: () => void | Promise<void>;
    cancelLabel?: string;
    confirmLabel?: string;
    /** Rizzui Button color for the confirm action (default: primary) */
    confirmColor?: 'primary' | 'danger' | 'secondary';
    placement?: 'top' | 'right' | 'bottom' | 'left';
    className?: string;
    actionIconClassName?: string;
    /** Icon inside the trigger button */
    triggerIcon?: ReactNode;
    /** Small icon next to the title (optional) */
    titleIcon?: ReactNode;
    contentWidthClassName?: string;
    actionSize?: 'sm' | 'md' | 'lg';
    /** Rizzui ActionIcon color (e.g. primary for toolbar actions) */
    actionColor?: 'primary' | 'danger' | 'secondary';
    /**
     * Si true: outline sin relleno (transparente) en el botón que abre el popover.
     * Si false: mismo estilo que antes (fondo blanco en el disparador).
     */
    triggerNoFill?: boolean;
};

/**
 * Popover de confirmación genérico (mismo patrón que DeletePopover) para acciones no destructivas o personalizadas.
 */
export default function ConfirmActionPopover({
    title,
    description,
    onConfirm,
    cancelLabel = 'No',
    confirmLabel = 'Sí',
    confirmColor = 'primary',
    placement = 'left',
    className,
    actionIconClassName,
    triggerIcon,
    titleIcon,
    contentWidthClassName = 'w-64 max-w-[min(100vw-2rem,22rem)]',
    actionSize = 'sm',
    actionColor,
    triggerNoFill = false,
}: ConfirmActionPopoverProps) {
    const icon =
        triggerIcon ??
        (actionSize === 'lg' ? (
            <HiOutlineCheck className="h-6 w-6" />
        ) : (
            <HiOutlineCheck className="h-4 w-4" />
        ));

    return (
        <Popover placement={placement} overlayClassName={cn('z-[1000]', className)}>
            <Popover.Trigger>
                <ActionIcon
                    size={actionSize}
                    color={actionColor}
                    variant="outline"
                    aria-label={title}
                    className={cn(
                        'cursor-pointer',
                        triggerNoFill
                            ? '!bg-transparent bg-transparent shadow-none hover:!bg-transparent'
                            : 'bg-white hover:!border-gray-900 hover:text-gray-700',
                        actionIconClassName
                    )}
                >
                    {icon}
                </ActionIcon>
            </Popover.Trigger>
            <Popover.Content className="z-[1000]">
                {({ setOpen }) => (
                    <div className={cn('pb-2 pt-1 text-center rtl:text-right', contentWidthClassName)}>
                        <Title
                            as="h6"
                            className={cn(
                                'mb-0.5 flex items-center justify-center text-sm text-gray-700',
                                titleIcon ? 'gap-1' : ''
                            )}
                        >
                            {titleIcon}
                            {title}
                        </Title>

                        <div className="flex justify-center py-1">
                            <IoWarningOutline className="text-amber-500" size={28} />
                        </div>

                        <Text className="mb-3 text-start leading-relaxed text-gray-600 text-sm">
                            {description}
                        </Text>
                        <div className="flex items-center justify-center gap-2">
                            <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(false)}>
                                {cancelLabel}
                            </Button>
                            <Button
                                size="sm"
                                color={confirmColor}
                                className="h-7"
                                onClick={() => {
                                    setOpen(false);
                                    void Promise.resolve(onConfirm());
                                }}
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                    </div>
                )}
            </Popover.Content>
        </Popover>
    );
}
